/**
 * documento.js — CPF ou CNPJ (inclusive o CNPJ ALFANUMÉRICO) num lugar só.
 *
 * Fonte única do Admin para limpar, mascarar e validar documento de pessoa física ou jurídica.
 * Quem precisa de "CPF ou CNPJ" (compra de pacote, compra de domínio, Configurações) usa ESTE
 * arquivo — nunca uma regex local. O `cpfValidator.js` continua existindo para o CPF do CLIENTE
 * FINAL (fichas de pessoa), que é sempre pessoa física.
 *
 * CNPJ alfanumérico (Receita Federal, vale para CNPJ novo desde jul/2026): as 12 primeiras
 * posições aceitam A–Z e 0–9; os 2 dígitos verificadores continuam numéricos. O cálculo é o mesmo
 * módulo 11 do CNPJ numérico, com o valor de cada caractere = código ASCII − 48 (0–9 → 0–9,
 * A → 17 … Z → 42). Para CNPJ só de números o resultado é idêntico ao algoritmo antigo.
 * Conferido nos exemplos oficiais: 12.ABC.345/01DE-35 e 11.222.333/0001-81.
 *
 * O documento LIMPO (o que vai para a API e para o banco) é: só [0-9A-Z], maiúsculo, sem máscara,
 * 11 caracteres (CPF) ou 14 (CNPJ). Cabe no `varchar(14)` de `users`.
 */

export const TIPO_DOCUMENTO = Object.freeze({ CPF: 'cpf', CNPJ: 'cnpj' });

const TAMANHO_CPF = 11;
const TAMANHO_CNPJ = 14;

const PESOS_CPF_1 = [10, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CPF_2 = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CNPJ_1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CNPJ_2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

// Onde entra cada separador da máscara, pela posição no documento limpo.
const MASCARA_CPF = { 3: '.', 6: '.', 9: '-' };
const MASCARA_CNPJ = { 2: '.', 5: '.', 8: '/', 12: '-' };

/**
 * Tira máscara, espaço e qualquer símbolo; põe em maiúsculas; corta em 14 caracteres.
 * @param {string|null|undefined} texto
 * @returns {string} ex.: '12.abc.345/01de-35' → '12ABC34501DE35'
 */
export function limparDocumento(texto) {
  if (!texto) return '';
  return String(texto).toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, TAMANHO_CNPJ);
}

/**
 * Diz o tipo pelo FORMATO (não confere dígito verificador).
 * @param {string} texto - com ou sem máscara
 * @returns {'cpf'|'cnpj'|null} null = incompleto ou impossível
 */
export function tipoDoDocumento(texto) {
  const limpo = limparDocumento(texto);
  if (limpo.length === TAMANHO_CPF && /^\d{11}$/.test(limpo)) return TIPO_DOCUMENTO.CPF;
  if (limpo.length === TAMANHO_CNPJ && /^[0-9A-Z]{12}\d{2}$/.test(limpo)) return TIPO_DOCUMENTO.CNPJ;
  return null;
}

// Módulo 11 com valor de caractere = ASCII − 48 (para dígito, é o próprio dígito).
function digitoVerificador(base, pesos) {
  let soma = 0;
  for (let i = 0; i < base.length; i++) soma += (base.charCodeAt(i) - 48) * pesos[i];
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}

// "00000000000", "AAAAAAAAAAAA00"… passam no módulo 11 por coincidência e não existem.
function todosIguais(base) {
  return /^(.)\1*$/.test(base);
}

/** @param {string} texto - com ou sem máscara */
export function isCpfValido(texto) {
  const limpo = limparDocumento(texto);
  if (!/^\d{11}$/.test(limpo) || todosIguais(limpo)) return false;
  const d1 = digitoVerificador(limpo.slice(0, 9), PESOS_CPF_1);
  const d2 = digitoVerificador(limpo.slice(0, 9) + d1, PESOS_CPF_2);
  return limpo.endsWith(`${d1}${d2}`);
}

/** @param {string} texto - com ou sem máscara; aceita CNPJ numérico e alfanumérico */
export function isCnpjValido(texto) {
  const limpo = limparDocumento(texto);
  if (!/^[0-9A-Z]{12}\d{2}$/.test(limpo) || todosIguais(limpo.slice(0, 12))) return false;
  const base = limpo.slice(0, 12);
  const d1 = digitoVerificador(base, PESOS_CNPJ_1);
  const d2 = digitoVerificador(base + d1, PESOS_CNPJ_2);
  return limpo.endsWith(`${d1}${d2}`);
}

/**
 * Validação completa, com a mensagem pronta para o campo (pt-BR).
 * @param {string} texto - o que a pessoa digitou
 * @returns {{ valido: boolean, tipo: 'cpf'|'cnpj'|null, limpo: string, erro: string|null }}
 */
export function validarDocumento(texto) {
  const limpo = limparDocumento(texto);

  if (!limpo) return { valido: false, tipo: null, limpo, erro: 'Informe o CPF ou o CNPJ.' };

  if (limpo.length === TAMANHO_CNPJ && /\D/.test(limpo.slice(12))) {
    return { valido: false, tipo: null, limpo, erro: 'Os dois últimos caracteres do CNPJ são sempre números.' };
  }

  const tipo = tipoDoDocumento(limpo);
  if (!tipo) {
    return { valido: false, tipo: null, limpo, erro: 'O CPF tem 11 números e o CNPJ tem 14 caracteres.' };
  }

  if (tipo === TIPO_DOCUMENTO.CPF) {
    return isCpfValido(limpo)
      ? { valido: true, tipo, limpo, erro: null }
      : { valido: false, tipo, limpo, erro: 'CPF inválido. Confira os números.' };
  }

  return isCnpjValido(limpo)
    ? { valido: true, tipo, limpo, erro: null }
    : { valido: false, tipo, limpo, erro: 'CNPJ inválido. Confira os caracteres.' };
}

function aplicarMascara(limpo, separadores) {
  let saida = '';
  for (let i = 0; i < limpo.length; i++) {
    if (i > 0 && separadores[i]) saida += separadores[i];
    saida += limpo[i];
  }
  return saida;
}

/**
 * Máscara progressiva para o campo, enquanto a pessoa digita.
 * Até 11 números → máscara de CPF (000.000.000-00). Passou de 11, ou apareceu letra → máscara
 * de CNPJ (XX.XXX.XXX/XXXX-00). Quem digita um CNPJ vê a máscara de CPF até o 11º caractere e
 * ela vira CNPJ no 12º — é o comportamento esperado de campo "CPF ou CNPJ".
 * @param {string} texto
 * @returns {string}
 */
export function mascararDocumento(texto) {
  const limpo = limparDocumento(texto);
  const pareceCpf = limpo.length <= TAMANHO_CPF && /^\d*$/.test(limpo);
  return aplicarMascara(limpo, pareceCpf ? MASCARA_CPF : MASCARA_CNPJ);
}
